import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Footer.css';
import TermsAndPrivacyModal from '../TermsAndPrivacyModal/TermsAndPrivacyModal';
import Chatbot from '../liveChatModule/liveChatModule';
import { getVersionLabel } from '../../config/appMeta';
import { TEXTS } from '../../constants/texts';
import { Info, Phone, Mail, ExternalLink } from 'lucide-react';
/* The footer sits on Oxford Blue, so it takes the white lock-up. */
import logo from '../../assets/Images/LOGOS/Docity_Logo_White.png';

const Footer = () => {
    const [token, setToken] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showBetaNote, setShowBetaNote] = useState(false);

    const { pathname } = useLocation();
    const openlink = () => {
        window.open('https://civil.docity.co.in')
    }
    useEffect(() => {
        const token = localStorage.getItem('token');
        setToken(token);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };
    useEffect(() => {
        scrollToTop();
    }, [pathname]);

    const handleLinkClick = (e, targetPath) => {
        if (pathname === targetPath) {
            e.preventDefault();
            scrollToTop();
            return;
        }
        scrollToTop();
    };


    return (
        <>
            <footer className="footer">
                <div className="footer__content container">

                    {/* Brand column. New — the footer previously opened straight
                        into a postal address with no indication whose site it is. */}
                    <div className="footer__section footer__section--brand">
                        <img src={logo} alt="DoCity" className="footer__logo" />
                        <p className="footer__tagline">
                            Civil engineer-led property verification, certification and
                            market valuation.
                        </p>
                    </div>

                    {/* Contact Section */}
                    <div className="footer__section">
                        <h2 className="footer__heading">{TEXTS.footer.contactUs}</h2>
                        <address className="footer__contact-info">
                            <p className="footer__contact-name">Maroju Sridhar Rao</p>
                            <p>M/S. Sridhar Constructions</p>
                            <p>Plot No.118, H.No.8-3-1069 1B, Doyen Villa, Srinagar Colony,</p>
                            <p>Hyderabad-500073, Telangana State.</p>
                            <div className="footer__contact-details">
                                {/* Now real tel:/mailto: links — these were plain text, so
                                    they could not be tapped on a phone. */}
                                <p>
                                    <Phone size={14} aria-hidden="true" />
                                    <a href="tel:+917893412266">+91 78934 12266</a>
                                    <span className="footer__sep">,</span>
                                    <a href="tel:+919948817779">+91 99488 17779</a>
                                </p>
                                <p>
                                    <Mail size={14} aria-hidden="true" />
                                    <a href="mailto:docityhelp@gmail.com">docityhelp@gmail.com</a>
                                </p>
                            </div>
                        </address>
                    </div>

                    <div className="footer__section">
                        <h2 className="footer__heading">{TEXTS.footer.quickLinks}</h2>

                        <nav className="footer__links" aria-label="Footer">
                            <NavLink
                                to="/about"
                                className="footer__link"
                                onClick={(e) => handleLinkClick(e, "/about")}
                            >
                                {TEXTS.footer.aboutUs}
                            </NavLink>

                            <NavLink
                                to="/faq"
                                className="footer__link"
                                onClick={(e) => handleLinkClick(e, "/faq")}
                            >
                                {TEXTS.footer.faq}
                            </NavLink>

                            {token && (
                                <NavLink
                                    to="/reqProperty"
                                    className="footer__link"
                                    onClick={(e) => handleLinkClick(e, "/reqProperty")}
                                >
                                    {TEXTS.footer.properties}
                                </NavLink>
                            )}

                            <NavLink
                                to="/contact"
                                className="footer__link"
                                onClick={(e) => handleLinkClick(e, "/contact")}
                            >
                                {TEXTS.footer.contactUsLink}
                            </NavLink>

                            {/* Leaves the app for the engineer portal, so it is marked
                                as an external destination. */}
                            <button
                                className="footer__link footer__link--btn"
                                onClick={openlink}
                                type="button"
                            >
                                {TEXTS.footer.engineerTask}
                                <ExternalLink size={13} aria-hidden="true" />
                            </button>
                        </nav>
                    </div>

                </div>

                {showModal && <TermsAndPrivacyModal onClose={() => setShowModal(false)} />}

                <div className="footer__bottom">
                    <p className="footer-bottom-text">
                        <span>© DoCity</span>
                        <span className="divider" aria-hidden="true">|</span>
                        <span aria-label="Application version">{TEXTS.footer.production}</span>
                        <span className="divider" aria-hidden="true">|</span>
                        <a
                            href="/#/TermsAndConditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="footer__bottom-link"
                        >
                            {TEXTS.footer.termsAndConditions}
                        </a>
                        <span className="divider" aria-hidden="true">|</span>
                        <span>{TEXTS.footer.rightsReserved}</span>
                    </p>
                    <div className="footer__copyright-marquee" aria-label="Copyright ownership lies with DoCity Civil Engineers Private Limited">
                        <div className="footer__copyright-track" aria-hidden="true">
                            <span>Copyright ownership lies with DoCity Civil Engineers Private Limited, India</span>
                            <span>Copyright ownership lies with DoCity Civil Engineers Private Limited, India</span>
                        </div>
                    </div>
                </div>


            </footer>

            {/* Private-beta notice. The trigger was a <button> whose only handler
                was preventDefault(), so it did nothing on click or keyboard — the
                tooltip was hover-only and unreachable without a mouse. It now
                toggles on click and closes on Escape. */}
            <div className={`footer-info-floating-wrapper${showBetaNote ? ' is-open' : ''}`}>
                <button
                    className="info-floating-btn"
                    type="button"
                    onClick={() => setShowBetaNote((v) => !v)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setShowBetaNote(false); }}
                    aria-expanded={showBetaNote}
                    aria-label="Private beta access notice"
                >
                    <Info size={20} aria-hidden="true" />
                </button>
                <div className="info-floating-tooltip" role="note">
                    <strong>Private Beta Access</strong>
                    <p>
                        This website is currently in a pre-release development phase and is
                        intended strictly for private circulation. Access is limited to a
                        select audience for testing and evaluation purposes.
                    </p>
                    <p>
                        <strong>Confidentiality:</strong> Please refrain from forwarding this
                        link or sharing any internal content. All intellectual property and
                        sole proprietorship rights are held by DoCity Civil Engineers Private
                        Limited. We value your partnership and look forward to your feedback as
                        we prepare for our public launch.
                    </p>
                </div>
            </div>
        </>
    );
};

export default Footer;

